// api/errors.js — 상태 사전의 **코드 정본**.
//
// 공통 상태 사전의 정본이 이 코드다 (설계 문서의 표는 2026-08-11 문서 정리로 삭제됐다).
// 요지: **이 표가 화면마다 갈리면 같은 실패가 화면마다 다르게 보인다.**
// 그래서 문장·다음 행위·범위(scope)를 화면이 각자 정하지 못하게 여기 한 곳에 둔다.
//
// QA v2 반려의 D-6(raw 이벤트/오류 코드를 사람 문장 자리에 그대로 씀)과
// D-4(권한 없는 Space에 셸을 그리고 거짓 문장 + raw 코드를 보여 줌)가 여기서 죽는다.

/**
 * 실패의 **범위**. 화면이 무엇을 지워야 하는지를 정한다.
 *
 *  - `session` : 전체. 세션 자체가 끝났다.
 *  - `wiring`  : 전체. 배선/서버 문제 — **재로그인을 권하면 안 된다.**
 *  - `surface` : 이 화면(pane). 셸은 남고 pane 자리만 실패로 그린다.
 *  - `region`  : 그 영역만. 나머지 화면은 계속 산다(부분 실패는 부분에 표시).
 *  - `command` : 방금 누른 행위만. 화면은 그대로 두고 그 자리에 알린다.
 */
export const SCOPE = Object.freeze({
  SESSION: 'session',
  WIRING: 'wiring',
  SURFACE: 'surface',
  REGION: 'region',
  COMMAND: 'command',
});

/**
 * 다음 행위. **문자열이 아니라 의도**다 — 버튼 라벨은 UI가 정하지만 어떤 길을 줄지는 여기서 정한다.
 *
 *  - `retry`      : 사람이 누르는 재시도
 *  - `retry_once` : 읽기의 **1회 자동** 재시도가 허용되는 유일한 경우(space_not_projected)
 *  - `relogin`    : 다시 로그인
 *  - `onboard`    : 사람 Node 선언으로
 *  - `select_node`: 후보 중 고르기
 *  - `join`       : 참여 신청 / 다른 Space
 *  - `reread`     : 다시 읽고 결정
 *  - `none`       : 사람이 할 수 있는 일이 없다. 재시도 버튼을 그리지 않는다
 */
export const NEXT = Object.freeze({
  RETRY: 'retry',
  RETRY_ONCE: 'retry_once',
  RELOGIN: 'relogin',
  ONBOARD: 'onboard',
  SELECT_NODE: 'select_node',
  JOIN: 'join',
  REREAD: 'reread',
  NONE: 'none',
});

/**
 * code → { 사람이 읽는 문장, 다음 행위, 범위 }
 *
 * 여기에 없는 code 는 **지어내지 않는다.** `describeError()`가 status 로만 일반화하고,
 * 원래 code 는 `technical` 로 따로 담아 접힌 자리에만 보인다(사람 문장 자리에 절대 안 나온다).
 */
const DICTIONARY = Object.freeze({
  // ── 세션 ────────────────────────────────────────────────────────────────
  session_invalid: {
    message: '세션이 만료되었습니다.',
    next: NEXT.RELOGIN,
    scope: SCOPE.SESSION,
  },
  session_expired: {
    message: '세션이 만료되었습니다.',
    next: NEXT.RELOGIN,
    scope: SCOPE.SESSION,
  },
  reauth_required: {
    message: '다시 로그인해야 하는 시점입니다. 세션 연장으로는 해결되지 않습니다.',
    next: NEXT.RELOGIN,
    scope: SCOPE.SESSION,
  },
  csrf_failed: {
    message: '요청이 이 화면에서 온 것인지 확인하지 못했습니다. 새로고침 후 다시 시도하세요.',
    next: NEXT.RETRY,
    scope: SCOPE.SESSION,
  },
  anchor_inactive: {
    message: '이 계정이 더 이상 활성 상태가 아닙니다.',
    next: NEXT.NONE,
    scope: SCOPE.SESSION,
  },

  // ── 배선 (재로그인을 권하지 않는다) ──────────────────────────────────────
  upstream_rejected_claim: {
    message: '서버 연결 설정에 문제가 있습니다. 재로그인으로 해결되지 않습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.WIRING,
    admin: true,
  },
  upstream_unavailable: {
    message: 'Loom 자체가 지금 응답하지 못합니다.',
    next: NEXT.RETRY,
    scope: SCOPE.WIRING,
  },
  authority_unavailable: {
    message: 'Loom 자체가 지금 응답하지 못합니다.',
    next: NEXT.RETRY,
    scope: SCOPE.WIRING,
  },
  gateway_unreachable: {
    message: 'Gateway에 닿지 못했습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.WIRING,
  },
  gateway_timeout: {
    message: 'Gateway가 제때 응답하지 않았습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.WIRING,
  },
  web_session_unavailable: {
    message: '이 배포에는 웹 로그인 경로가 아직 설정되지 않았습니다.',
    next: NEXT.NONE,
    scope: SCOPE.WIRING,
    admin: true,
  },

  // ── 진입 (사람 Node 해소) ───────────────────────────────────────────────
  human_node_absent: {
    message: '아직 사람 Node가 없습니다.',
    next: NEXT.ONBOARD,
    scope: SCOPE.SURFACE,
  },
  human_node_selection_required: {
    message: '여러 사람 Node가 있습니다. 하나를 고르세요.',
    next: NEXT.SELECT_NODE,
    scope: SCOPE.SURFACE,
  },
  human_node_not_selectable: {
    message: '이 세션으로는 그 Node를 쓸 수 없습니다.',
    next: NEXT.SELECT_NODE,
    scope: SCOPE.SURFACE,
  },
  human_node_already_exists: {
    message: '이미 사람 Node가 있습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.SURFACE,
  },

  // ── 권한 · 경계 ─────────────────────────────────────────────────────────
  forbidden: {
    message: '이 자리를 읽을 권한이 없습니다.',
    next: NEXT.JOIN,
    scope: SCOPE.SURFACE,
  },
  space_not_projected: {
    // 권한 오류로 말하지 않는다 — 아직 준비 중인 것이다.
    message: '이 Space의 권한 정보가 자료 서비스에 아직 반영되지 않았습니다.',
    next: NEXT.RETRY_ONCE,
    scope: SCOPE.SURFACE,
  },
  projection_lag: {
    message: '방금의 변경이 아직 반영되지 않았습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.SURFACE,
  },
  space_archived: {
    // 화면은 이 코드를 보면 **쓰기 UI 자체를 제거**한다.
    message: '보관된 Space입니다. 기록은 읽을 수 있고 새 기록은 남길 수 없습니다.',
    next: NEXT.NONE,
    scope: SCOPE.SURFACE,
  },
  stale_claim: {
    message: '권한이 그사이 바뀌었습니다. 다시 읽고 결정하세요.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  human_direct_required: {
    // 화면은 이 상황을 **오류로 만나지 않는다** — 버튼을 미리 그리지 않기 때문이다.
    // 그래도 도달하면 정직하게 말한다.
    message: '이 판단은 사람이 직접 하는 세션에서만 가능합니다.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },
  node_not_bound_to_session: {
    message: '이 세션은 그 Node로 말할 수 없습니다.',
    next: NEXT.NONE,
    scope: SCOPE.SESSION,
  },
  provenance_not_client_supplied: {
    // 사용자 잘못이 아니라 클라이언트 버그다. 정직하게 그렇게 말한다.
    message: '화면이 보내면 안 되는 값을 보냈습니다. 이 화면의 결함입니다.',
    next: NEXT.NONE,
    scope: SCOPE.WIRING,
    admin: true,
  },

  // ── 대화 원문 provider (그 영역만 죽는다) ────────────────────────────────
  provider_unavailable: {
    message: '원문 대화 서비스에 닿지 못했습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.REGION,
  },
  provider_access_denied: {
    message: '원문 제공자가 이 접근을 거절했습니다.',
    next: NEXT.NONE,
    scope: SCOPE.REGION,
  },
  provider_not_configured: {
    message: '이 배포에는 대화 원문 통로가 설정되어 있지 않습니다.',
    next: NEXT.NONE,
    scope: SCOPE.REGION,
  },
  provider_append_unsupported: {
    message: '이 원문 통로에는 새 기록을 남길 수 없습니다.',
    next: NEXT.NONE,
    scope: SCOPE.REGION,
  },
  unknown_provider: {
    message: '이 배포가 모르는 대화 통로입니다.',
    next: NEXT.NONE,
    scope: SCOPE.REGION,
  },

  // ── 멱등 · 경합 ─────────────────────────────────────────────────────────
  request_in_flight: {
    // 같은 키로 재전송하지 않는다 — 그래서 next 는 reread 다.
    message: '같은 요청이 이미 처리 중입니다.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  idempotency_key_reused: {
    message: '같은 요청 번호가 다른 내용으로 다시 왔습니다. 다시 읽고 결정하세요.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  proposal_base_stale: {
    message: '이 제안이 기준으로 삼은 공동 이해가 그사이 바뀌었습니다. 지금 내용을 다시 보고 결정하세요.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  already_responded: {
    message: '이미 이 제안에 응답했습니다.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  request_version_mismatch: {
    message: '이 신청이 그사이 바뀌었습니다. 다시 읽고 결정하세요.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  join_request_decided: {
    message: '이미 처리된 신청입니다.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  already_member: {
    message: '이미 이 Space의 참여자입니다.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  thread_closed: {
    message: '닫힌 대화입니다. 새 기록을 남길 수 없습니다.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },

  // ── 클라이언트 결함 계열 ────────────────────────────────────────────────
  invalid_field: {
    message: '입력한 값을 서버가 받지 못했습니다.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },
  // 자료 주소에 자격이 들어 있다. **무엇이 문제인지는 말하고, 그 값은 되풀이하지 않는다** —
  // 거절된 것이 토큰이면 화면에 다시 그리는 순간 거절한 의미가 없어진다.
  asset_source_credentialed_uri: {
    message: '주소에 토큰·비밀번호처럼 보이는 값이 들어 있어 저장하지 않았습니다. 자격 없는 주소를 쓰세요.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },
  asset_source_repository_not_identity: {
    message: '저장소 주소에는 물음표 뒤(쿼리)나 # 뒤를 붙이지 않습니다. 판·파일·줄은 아래 칸에 적습니다.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },
  asset_source_path_traversal: {
    message: '저장소 밖을 가리키는 경로입니다. 저장소 기준 상대 경로를 적으세요.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },
  asset_source_path_not_relative: {
    message: '절대 경로는 저장할 수 없습니다. 저장소 기준 상대 경로를 적으세요.',
    next: NEXT.NONE,
    scope: SCOPE.COMMAND,
  },
  // 넘김을 쓰는 동안 공동 이해가 앞서갔다. 화면은 초안을 지키고 사람이 고르게 한다.
  handoff_base_stale: {
    message: '이 Task의 공동 이해가 그사이 바뀌었습니다. 새 이해를 확인한 뒤 다시 보내세요.',
    next: NEXT.REREAD,
    scope: SCOPE.COMMAND,
  },
  unknown_field: {
    message: '화면이 서버가 모르는 값을 보냈습니다. 이 화면의 결함입니다.',
    next: NEXT.NONE,
    scope: SCOPE.WIRING,
    admin: true,
  },
  csrf_unavailable: {
    message: '세션 준비가 아직 끝나지 않았습니다.',
    next: NEXT.RETRY,
    scope: SCOPE.SESSION,
  },
});

/** status 만 아는 경우의 일반화. **code 를 지어내지 않는다.** */
const BY_STATUS = Object.freeze({
  400: { message: '요청을 서버가 받아들이지 않았습니다.', next: NEXT.NONE, scope: SCOPE.COMMAND },
  401: { message: '세션이 만료되었습니다.', next: NEXT.RELOGIN, scope: SCOPE.SESSION },
  403: { message: '이 자리를 읽을 권한이 없습니다.', next: NEXT.JOIN, scope: SCOPE.SURFACE },
  404: { message: '여기에는 그런 자리가 없습니다.', next: NEXT.NONE, scope: SCOPE.SURFACE },
  409: { message: '그사이 상태가 바뀌었습니다. 다시 읽고 결정하세요.', next: NEXT.REREAD, scope: SCOPE.COMMAND },
  429: { message: '요청이 너무 잦습니다. 잠시 뒤 다시 시도하세요.', next: NEXT.RETRY, scope: SCOPE.COMMAND },
  500: { message: 'Loom 자체가 지금 응답하지 못합니다.', next: NEXT.RETRY, scope: SCOPE.WIRING },
  502: { message: '서버 연결 설정에 문제가 있습니다. 재로그인으로 해결되지 않습니다.', next: NEXT.RETRY, scope: SCOPE.WIRING, admin: true },
  503: { message: 'Loom 자체가 지금 응답하지 못합니다.', next: NEXT.RETRY, scope: SCOPE.WIRING },
  504: { message: 'Gateway가 제때 응답하지 않았습니다.', next: NEXT.RETRY, scope: SCOPE.WIRING },
});

const FALLBACK = Object.freeze({
  message: '알 수 없는 문제가 생겼습니다.',
  next: NEXT.RETRY,
  scope: SCOPE.SURFACE,
});

/** Gateway 가 준 오류를 그대로 담는 타입 있는 에러. */
export class ProductError extends Error {
  constructor({ code, status, message, details }) {
    super(message || code || 'product_error');
    this.name = 'ProductError';
    this.code = code ?? null;
    this.status = status ?? null;
    this.details = details ?? null;
  }
}

/**
 * 어떤 실패든 화면이 쓸 수 있는 하나의 모양으로 만든다.
 *
 * @returns {{
 *   code: string|null,        // 원래 코드. **사람 문장 자리에 쓰지 않는다**
 *   status: number|null,
 *   message: string,          // 사람이 읽는 문장
 *   next: string,             // NEXT.*
 *   scope: string,            // SCOPE.*
 *   admin: boolean,           // 관리자 안내를 함께 보여야 하나
 *   candidates: Array|null,   // human_node_selection_required 의 후보
 *   technical: string|null,   // 접힌 자리에만 보이는 원문 (code · status)
 * }}
 */
export function describeError(error) {
  const code = error?.code ?? null;
  const status = error?.status ?? null;

  const entry =
    (code && DICTIONARY[code]) ||
    (status && BY_STATUS[status]) ||
    FALLBACK;

  return {
    code,
    status,
    message: entry.message,
    next: entry.next,
    scope: entry.scope,
    admin: entry.admin === true,
    candidates: candidatesOf(error),
    technical: technicalOf(code, status),
  };
}

function candidatesOf(error) {
  const list = error?.details?.candidates;
  return Array.isArray(list) && list.length > 0 ? list : null;
}

function technicalOf(code, status) {
  if (!code && !status) return null;
  return [status, code].filter(Boolean).join(' · ');
}

/** 이 실패가 세션 전체를 끝냈나 — 셸을 그릴지 로그인 화면을 그릴지 가른다. */
export function isSessionEnded(error) {
  return describeError(error).scope === SCOPE.SESSION;
}

/**
 * 읽기의 **1회 자동 재시도**가 허용되는 유일한 경우.
 * ux §5 공통 규칙 2: 자동 재전송 금지, 단 `space_not_projected` 만 예외.
 */
export function allowsAutoRetry(error) {
  return describeError(error).next === NEXT.RETRY_ONCE;
}

/** 쓰기 UI 자체를 없애야 하는 상태인가. */
export function isWriteClosed(error) {
  return error?.code === 'space_archived';
}
